<template>
  <div v-if="currentTeam || isDemo" class="flex h-full min-h-0 flex-col">
    <div
      v-if="isLocal && !isDemo"
      class="grid shrink-0 transition-[grid-template-rows,opacity] duration-200 motion-reduce:transition-none"
      :class="rail ? 'pointer-events-none grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'"
      :inert="rail">
      <div class="min-h-0 overflow-hidden">
        <div
          class="mx-2.5 mb-1 cursor-pointer rounded-md bg-yellow-400 px-2 py-0.5 text-center text-xs whitespace-nowrap hover:bg-yellow-500 dark:bg-yellow-900 dark:hover:bg-yellow-800"
          title="Click to copy config to clipboard"
          @click="copyConfigToClipboard">
          Team #{{ currentTeam?.id }}
        </div>
      </div>
    </div>

    <nav class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-2.5 pt-1.5 pb-7" aria-label="Main">
      <router-link
        v-if="settingsScope"
        v-tooltip.right="rail ? 'Back to app' : null"
        :to="{ name: ROUTES.DASHBOARD, params: { teamPid } }"
        class="mb-2.5 flex h-[31px] items-center gap-2.5 rounded-md px-2.5 text-sm font-medium text-ink hover:bg-tint hover:text-primary">
        <BackIcon class="size-4 shrink-0 text-muted" aria-hidden="true" />
        <span
          class="whitespace-nowrap transition-opacity duration-150 motion-reduce:transition-none"
          :class="{ 'opacity-0': rail }">
          Back to app
        </span>
      </router-link>

      <div
        v-else-if="currentTeam?.managed_by?.name"
        class="grid transition-[grid-template-rows,opacity] duration-200 motion-reduce:transition-none"
        :class="rail ? 'pointer-events-none grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'"
        :inert="rail">
        <div class="min-h-0 overflow-hidden">
          <a
            :href="`mailto:${currentTeam.managed_by.email}?subject=RightMessage`"
            class="mb-2.5 flex items-center gap-2.5 rounded-md p-1.5 text-copy hover:bg-tint hover:text-primary">
            <span class="flex size-8 shrink-0 items-center justify-center rounded bg-action-soft">
              <img
                v-if="currentTeam.managed_by.logo"
                :src="currentTeam.managed_by.logo"
                alt=""
                class="size-5 rounded-sm object-contain" />
              <BuildingIcon v-else class="size-4 text-action" aria-hidden="true" />
            </span>
            <span class="min-w-0 text-[11.5px] leading-[15px] whitespace-nowrap">
              Managed by
              <span class="block truncate font-semibold">{{ currentTeam.managed_by.name }}</span>
            </span>
          </a>
        </div>
      </div>

      <div
        v-if="settingsScope"
        class="mb-2.5 flex items-center gap-2.5 px-1.5 py-1"
        :title="rail ? scopeHeader.title : undefined">
        <span
          v-if="settingsScope === 'project'"
          class="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary-soft text-[13px] font-bold text-primary"
          aria-hidden="true">
          {{ scopeHeader.title.charAt(0).toUpperCase() }}
        </span>
        <span
          v-else
          class="flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-600 text-[11px] font-bold text-white"
          aria-hidden="true">
          {{ initials }}
        </span>
        <span
          class="min-w-0 transition-opacity duration-150 motion-reduce:transition-none"
          :class="{ 'opacity-0': rail }">
          <span class="block truncate text-sm leading-[18px] font-bold text-heading">{{ scopeHeader.title }}</span>
          <span class="block truncate text-xs leading-4 text-copy">{{ scopeHeader.subtitle }}</span>
        </span>
      </div>

      <div v-for="group in groups" :key="group.label ?? 'top'" class="mb-2.5">
        <button
          v-if="group.label"
          type="button"
          class="relative flex h-6 w-full items-center justify-between px-2.5 text-[11px] font-bold text-muted hover:text-primary"
          :aria-expanded="isGroupOpen(group)"
          :disabled="rail"
          @click="toggleGroup(group.label)">
          <span
            class="absolute left-2.5 w-4 border-t border-line-strong transition-opacity duration-150 motion-reduce:transition-none"
            :class="rail ? 'opacity-100' : 'opacity-0'"
            aria-hidden="true" />
          <span
            class="whitespace-nowrap transition-opacity duration-150 motion-reduce:transition-none"
            :class="{ 'opacity-0': rail }">
            {{ group.label }}
          </span>
          <ChevronDownIcon
            class="size-[11px] shrink-0 transition-[transform,opacity] duration-150 motion-reduce:transition-none"
            :class="{ '-rotate-90': !isGroupOpen(group), 'opacity-0': rail }"
            aria-hidden="true" />
        </button>
        <ul v-show="isGroupOpen(group)" role="list" class="flex flex-col gap-px">
          <li v-for="item in group.items" :key="item.name">
            <a
              v-if="item.href"
              v-tooltip.right="rail ? itemTooltip(item) : null"
              :href="item.href"
              class="flex h-[31px] items-center gap-2.5 rounded-md px-2.5 text-sm font-medium text-ink hover:bg-tint hover:text-primary">
              <component :is="item.icon" v-if="item.icon" class="size-4 shrink-0 text-muted" aria-hidden="true" />
              <span
                class="whitespace-nowrap transition-opacity duration-150 motion-reduce:transition-none"
                :class="{ 'opacity-0': rail }">
                {{ item.name }}
              </span>
            </a>
            <router-link
              v-else
              v-tooltip.right="rail ? itemTooltip(item) : null"
              :to="item.comingsoon ? {} : { ...item.to, params: { teamPid }, ...(item.query || {}) }"
              class="relative flex h-[31px] items-center gap-2.5 rounded-md px-2.5 text-sm"
              :class="[
                isCurrentRoute(item)
                  ? 'bg-tint font-semibold text-primary'
                  : 'font-medium text-ink hover:bg-tint hover:text-primary',
                { 'cursor-not-allowed': item.comingsoon }
              ]">
              <span class="flex min-w-0 flex-1 items-center gap-2.5" :class="{ 'opacity-50': isItemLocked(item) }">
                <component
                  :is="item.icon"
                  v-if="item.icon"
                  class="size-4 shrink-0"
                  :class="isCurrentRoute(item) ? 'text-primary' : 'text-muted'"
                  aria-hidden="true" />
                <span
                  class="truncate whitespace-nowrap transition-opacity duration-150 motion-reduce:transition-none"
                  :class="{ 'opacity-0': rail }">
                  {{ item.name }}
                </span>
                <span
                  v-if="item.aside"
                  class="whitespace-nowrap transition-opacity duration-150 motion-reduce:transition-none"
                  :class="{ 'opacity-0': rail }"
                  v-html="item.aside" />
              </span>
              <span
                v-if="itemTag(item)"
                class="shrink-0 rounded bg-action-soft px-1.5 py-0.5 text-[10.5px] font-bold whitespace-nowrap text-action transition-opacity duration-150 motion-reduce:transition-none"
                :class="{ 'opacity-0': rail }">
                {{ itemTag(item) }}
              </span>
              <span
                v-if="item.badge"
                class="pointer-events-none absolute top-1.5 left-[23px] size-1.5 rounded-full bg-action transition-opacity duration-150 motion-reduce:transition-none"
                :class="rail ? 'opacity-100' : 'opacity-0'"
                aria-hidden="true" />
            </router-link>

            <ul
              v-if="!rail && item.children?.length && isCurrentRoute(item)"
              role="list"
              class="my-0.5 ml-[18px] border-l border-line pl-[15px]">
              <li v-for="child in item.children" :key="child.name">
                <router-link
                  :to="{ ...child.to, params: { teamPid } }"
                  class="block cursor-pointer py-1 text-sm"
                  :class="isCurrentRoute(child) ? 'font-semibold text-primary' : 'text-copy hover:text-primary'">
                  {{ child.name }}
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <p v-if="managedByNote && !rail" class="mx-2.5 mt-1 text-xs leading-4 text-copy" data-test="settings-managed-by">
        {{ managedByNote }}
      </p>

      <template v-if="settingsScope === 'account'">
        <div class="mx-2.5 my-2.5 border-t border-line" aria-hidden="true" />
        <router-link
          v-tooltip.right="rail ? 'Sign out' : null"
          :to="{ name: ROUTES.LOGOUT }"
          class="flex h-[31px] items-center gap-2.5 rounded-md px-2.5 text-sm font-medium text-ink hover:bg-tint hover:text-primary">
          <SignOutIcon class="size-4 shrink-0 text-muted" aria-hidden="true" />
          <span
            class="whitespace-nowrap transition-opacity duration-150 motion-reduce:transition-none"
            :class="{ 'opacity-0': rail }">
            Sign out
          </span>
        </router-link>
      </template>
    </nav>

    <div class="relative px-2.5 pb-2.5">
      <div
        class="pointer-events-none absolute inset-x-0 -top-7 h-7 bg-linear-to-b from-transparent to-overlay dark:to-canvas"
        aria-hidden="true" />
      <div
        v-if="!settingsScope"
        class="grid transition-[grid-template-rows,opacity] duration-200 motion-reduce:transition-none"
        :class="rail ? 'pointer-events-none grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'"
        :inert="rail">
        <div class="min-h-0 overflow-hidden">
          <SidebarPromo />
        </div>
      </div>
      <router-link
        v-else
        v-tooltip.right="rail ? crossLink.label : null"
        :to="crossLink.to"
        class="flex h-[31px] items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium text-ink hover:bg-tint hover:text-primary"
        data-test="settings-cross-link">
        <span
          class="min-w-0 flex-1 truncate whitespace-nowrap transition-opacity duration-150 motion-reduce:transition-none"
          :class="{ 'w-0 flex-none opacity-0': rail }">
          {{ crossLink.label }}
        </span>
        <ArrowRightIcon class="size-4 shrink-0 text-muted" aria-hidden="true" />
      </router-link>
      <SidebarTrialStatus :rail="rail" :class="rail ? '-mx-2.5 mt-2.5' : 'mt-2.5'" />
      <div
        class="mt-2 flex items-center gap-0.5"
        :class="{ 'flex-col': rail, 'border-t border-line pt-2': settingsScope }">
        <router-link
          v-if="!isDemo"
          v-tooltip.right="rail ? 'Your account settings' : null"
          :to="{ name: ROUTES.ACCOUNT, params: { teamPid } }"
          class="flex min-w-0 items-center gap-2 overflow-hidden rounded-md p-1"
          :class="[
            rail ? 'order-3 w-[34px] shrink-0' : 'flex-1',
            settingsScope === 'account' ? 'bg-overlay text-primary' : 'text-ink hover:bg-tint'
          ]"
          :aria-current="settingsScope === 'account' ? 'page' : undefined"
          :title="rail ? undefined : 'Your account settings'"
          aria-label="Your account settings">
          <span
            class="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-slate-600 text-[10px] font-bold text-white"
            aria-hidden="true">
            {{ initials }}
          </span>
          <span
            class="truncate text-[13px] font-semibold whitespace-nowrap transition-opacity duration-150 motion-reduce:transition-none"
            :class="{ 'opacity-0': rail }">
            {{ firstName }}
          </span>
        </router-link>
        <div v-else-if="!rail" class="flex-1" />
        <router-link
          v-if="!isDemo"
          v-tooltip.right="rail ? 'Project settings' : null"
          :to="{ name: ROUTES.SETTINGS, params: { teamPid } }"
          class="flex size-[30px] shrink-0 items-center justify-center rounded-md"
          :class="[
            { 'order-2': rail },
            settingsScope === 'project' ? 'bg-overlay text-primary' : 'text-ink hover:bg-tint hover:text-primary'
          ]"
          :aria-current="settingsScope === 'project' ? 'page' : undefined"
          :title="rail ? undefined : 'Project settings'"
          aria-label="Project settings">
          <SettingsIcon class="size-[18px]" aria-hidden="true" />
        </router-link>
        <button
          v-tooltip.right="rail ? (isDark ? 'Switch to light mode' : 'Switch to dark mode') : null"
          type="button"
          class="flex size-[30px] shrink-0 items-center justify-center rounded-md text-ink hover:bg-tint hover:text-primary"
          :class="{ 'order-1': rail }"
          :title="rail ? undefined : isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          @click="toggleTheme">
          <SunIcon v-if="isDark" class="size-[18px]" aria-hidden="true" />
          <MoonIcon v-else class="size-[18px]" aria-hidden="true" />
        </button>
        <button
          v-if="canToggle"
          v-tooltip.right="rail ? 'Expand sidebar' : null"
          type="button"
          class="flex size-[30px] shrink-0 items-center justify-center rounded-md text-ink hover:bg-tint hover:text-primary"
          :class="{ 'order-first': rail }"
          :title="rail ? 'Expand sidebar' : 'Collapse sidebar'"
          :aria-label="rail ? 'Expand sidebar' : 'Collapse sidebar'"
          @click="toggle">
          <SidebarExpandIcon v-if="rail" class="size-[18px]" aria-hidden="true" />
          <SidebarCollapseIcon v-else class="size-[18px]" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { shellIcons } from '@dashboard/components/icons/shellIcons'
import SidebarPromo from '@dashboard/components/layouts/SidebarPromo.vue'
import SidebarTrialStatus from '@dashboard/components/layouts/SidebarTrialStatus.vue'
import { useFeatureGating } from '@dashboard/composables/useFeatureGating'
import { useSidebarCollapse } from '@dashboard/composables/useSidebarCollapse'
import { useThemePreference } from '@dashboard/composables/useThemePreference'
import { useAccountSettingsNavigation, useProjectSettingsNavigation } from '@dashboard/data/settingsNavigation'
import { useSidebarNavigation } from '@dashboard/data/sidebarNavigation'
import { ROUTES } from '@dashboard/router/routeNames'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useNotificationStore } from '@dashboard/stores/notificationStore'
import { usePermissionStore } from '@dashboard/stores/permissionStore'
import { useTeamStore } from '@dashboard/stores/teamStore'
import { wordInitials } from '@dashboard/utils/initials'
import { useLocalStorage } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import type { Component } from 'vue'
import type { RouteLocationNamedRaw } from 'vue-router'
import { useRoute } from 'vue-router'

const {
  arrowLeft: BackIcon,
  arrowRight: ArrowRightIcon,
  building: BuildingIcon,
  sidebarCollapse: SidebarCollapseIcon,
  sidebarExpand: SidebarExpandIcon,
  chevronDown: ChevronDownIcon,
  settings: SettingsIcon,
  signOut: SignOutIcon,
  moon: MoonIcon,
  sun: SunIcon
} = shellIcons

type SidebarNavigationItem = {
  name: string
  type?: 'divider'
  href?: string
  to: RouteLocationNamedRaw
  icon?: Component
  comingsoon?: boolean
  query?: Record<string, unknown>
  permission?: string
  requiredFeature?: string
  aside?: string
  beta?: boolean
  /** A short label such as "New", shown beside the name and as a dot on the icon rail. */
  badge?: string
  /** Listed beneath this item only while it is current; their routes keep it current. */
  children?: SidebarNavigationItem[]
  /** Detail routes that keep this item current. */
  childRoutes?: string[]
}

type NavGroup = { label: string | null; items: SidebarNavigationItem[] }

const props = withDefaults(defineProps<{ rail?: boolean; canToggle?: boolean }>(), {
  rail: false,
  canToggle: false
})
const { toggle } = useSidebarCollapse()

const route = useRoute()
const isLocal = import.meta.env.DEV

const { isDark, toggle: toggleThemePreference } = useThemePreference()
const toggleTheme = () => {
  toggleThemePreference().catch(() => {
    notificationStore.addError('We couldn’t save your theme to your account. It still applies in this browser.')
  })
}

const { canAccess } = usePermissionStore()
const { isFeatureLocked, requiredTierForFeature, tierDisplayName } = useFeatureGating()

const { user } = storeToRefs(useAuthStore())
const configStore = useConfigStore()
const { currentTeam, currentTeamPid, owner, config } = storeToRefs(configStore)
const notificationStore = useNotificationStore()
const { isDemo } = storeToRefs(useTeamStore())

const teamPid = computed(() => route.params.teamPid || currentTeamPid.value || 'demo')

const isItemLocked = (item: SidebarNavigationItem) =>
  !isDemo.value &&
  ((!!item.permission && !canAccess(item.permission)) ||
    (!!item.requiredFeature && isFeatureLocked(item.requiredFeature)))

const itemTag = (item: SidebarNavigationItem) => {
  if (isItemLocked(item)) {
    return item.requiredFeature ? tierDisplayName(requiredTierForFeature(item.requiredFeature)) : 'Upgrade'
  }
  if (item.comingsoon && !isDemo.value) {
    return 'Soon'
  }
  return item.beta ? 'Beta' : (item.badge ?? null)
}

const itemTooltip = (item: SidebarNavigationItem) => {
  const tag = itemTag(item)
  return tag ? `${item.name} · ${tag}` : item.name
}

const settingsScope = computed(() => route.meta?.settingsScope ?? null)
const navigation = useSidebarNavigation()
const projectSettingsNavigation = useProjectSettingsNavigation()
const accountSettingsNavigation = useAccountSettingsNavigation()

const groups = computed<NavGroup[]>(() => {
  if (settingsScope.value === 'project') {
    return [{ label: null, items: projectSettingsNavigation.value }]
  }
  if (settingsScope.value === 'account') {
    const { account, agency } = accountSettingsNavigation.value
    return agency.length > 0
      ? [
          { label: null, items: account },
          { label: 'Agency', items: agency }
        ]
      : [{ label: null, items: account }]
  }

  const result: NavGroup[] = [{ label: null, items: [] }]
  for (const item of navigation.value as SidebarNavigationItem[]) {
    if (item.type === 'divider') {
      result.push({ label: item.name || null, items: [] })
    } else {
      result[result.length - 1]?.items.push(item)
    }
  }
  return result.filter((group) => group.items.length > 0)
})

const projectName = computed(() => currentTeam.value?.name?.trim() || 'This project')

const scopeHeader = computed(() =>
  settingsScope.value === 'account'
    ? { title: 'Your account', subtitle: user.value?.email ?? '' }
    : { title: projectName.value, subtitle: 'Project settings' }
)

// Collaborators never see Collaborators or Plan & billing, so say who handles them.
const managedByNote = computed(() =>
  settingsScope.value === 'project' && !canAccess('manage-collaborators') && owner.value?.email
    ? `Billing and collaborators are managed by ${owner.value.email}.`
    : null
)

const crossLink = computed(() =>
  settingsScope.value === 'account'
    ? { label: `${projectName.value} settings`, to: { name: ROUTES.SETTINGS, params: { teamPid: teamPid.value } } }
    : { label: 'Your account settings', to: { name: ROUTES.ACCOUNT, params: { teamPid: teamPid.value } } }
)

const collapsedGroups = useLocalStorage<string[]>(`rm_sidebar_collapsed_groups_${user.value?.id ?? 'guest'}`, [])

const isGroupOpen = (group: NavGroup) => props.rail || !group.label || !collapsedGroups.value.includes(group.label)

const toggleGroup = (label: string) => {
  collapsedGroups.value = collapsedGroups.value.includes(label)
    ? collapsedGroups.value.filter((collapsed) => collapsed !== label)
    : [...collapsedGroups.value, label]
}

const initials = computed(() => wordInitials(user.value?.name ?? user.value?.email ?? ''))
const firstName = computed(() => user.value?.name?.split(/\s+/)[0] ?? user.value?.email ?? '')

const copyConfigToClipboard = async () => {
  try {
    if (!config.value) {
      notificationStore.addError('No config data available to copy')
      return
    }

    await navigator.clipboard.writeText(JSON.stringify(config.value, null, 2))
    notificationStore.addNotification('Configuration copied to clipboard')
  } catch (error) {
    console.error('Failed to copy config:', error)
    notificationStore.addError('Failed to copy config to clipboard')
  }
}

const PARENT_ROUTE_CHILDREN: Record<string, string[]> = {
  [ROUTES.SEGMENTGROUPS]: [ROUTES.SEGMENTGROUP],
  [ROUTES.QUESTIONS]: [ROUTES.QUESTION],
  [ROUTES.OFFERFUNNELS]: [ROUTES.OFFERFUNNEL, ROUTES.CREATEOFFERFUNNEL],
  [ROUTES.FORMS]: [ROUTES.FORM],
  [ROUTES.OFFERS]: [ROUTES.OFFER],
  [ROUTES.WIDGETS]: [ROUTES.WIDGET, ROUTES.CREATEWIDGET],
  [ROUTES.CAMPAIGNS]: [ROUTES.CREATECAMPAIGN, ROUTES.CAMPAIGN],
  [ROUTES.EMAILS]: [ROUTES.EMAIL],
  [ROUTES.GOALS]: [ROUTES.CREATEGOAL, ROUTES.GOAL, ROUTES.EDITGOAL],
  [ROUTES.PEOPLE]: [ROUTES.VISITOR_DETAIL, ROUTES.LEAD_ALERTS]
}

function isCurrentRoute(item: SidebarNavigationItem): boolean {
  const itemRouteName = typeof item.to?.name === 'string' ? item.to.name : ''
  const currentRouteName = typeof route.name === 'string' ? route.name : ''
  if (!itemRouteName) {
    return false
  }
  return (
    itemRouteName === currentRouteName ||
    !!item.childRoutes?.includes(currentRouteName) ||
    !!PARENT_ROUTE_CHILDREN[itemRouteName]?.includes(currentRouteName) ||
    !!item.children?.some((child) => child.to.name === currentRouteName)
  )
}
</script>
